import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  FC,
  ReactNode,
} from 'react';

import { APP_THEME_FF } from 'constants/defaultConstants';
import {
  hasStoredAppTheme,
  resolveAppTheme,
} from 'utils/theme';

type ContextProps = {
  darkTheme: boolean;
  setDarkTheme: (darkTheme: boolean) => void;
  toggleTheme: () => void;
};

type ThemeContextProviderProps = {
  children: ReactNode;
};

const applyThemeToDOM = (mode: boolean) => {
  document
    .querySelector('body')
    ?.setAttribute('data-theme', mode ? 'dark' : 'light');
  document.querySelector('body')?.classList.toggle('dark-mode', mode);
  document.querySelector('.app-header')?.classList.toggle('is-dark', mode);
  document.querySelector('.app-header')?.classList.toggle('is-light', !mode);
  window.dispatchEvent(new Event('storage'));
};

const ThemeContext = createContext<ContextProps>({
  darkTheme: false,
  setDarkTheme: () => {},
  toggleTheme: () => {},
});

export const ThemeProvider: FC<ThemeContextProviderProps> = ({ children }) => {
  const [darkTheme, setDarkThemeState] = useState(() => resolveAppTheme());
  const hasUserPreferenceRef = useRef(hasStoredAppTheme());

  const setTheme = useCallback((mode: boolean, persist = false) => {
    if (persist) {
      localStorage.setItem(APP_THEME_FF, mode ? 'dark' : 'light');
      hasUserPreferenceRef.current = true;
    }
    setDarkThemeState(mode);
    applyThemeToDOM(mode);
  }, []);

  const setDarkTheme = useCallback(
    (mode: boolean) => {
      setTheme(mode, true);
    },
    [setTheme]
  );

  const toggleTheme = useCallback(() => {
    setDarkThemeState((current) => {
      const next = !current;
      localStorage.setItem(APP_THEME_FF, next ? 'dark' : 'light');
      hasUserPreferenceRef.current = true;
      applyThemeToDOM(next);
      return next;
    });
  }, []);

  useEffect(() => {
    applyThemeToDOM(darkTheme);
  }, []);

  useEffect(() => {
    if (hasUserPreferenceRef.current) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      if (hasStoredAppTheme()) {
        return;
      }
      setTheme(event.matches, false);
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [setTheme]);

  const value = {
    darkTheme,
    toggleTheme,
    setDarkTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export default ThemeContext;
